/**
 * <ActivityDetailsModal> — la fiche d'une activité (template).
 *
 * Ce que le parent voit quand il touche une activité de la liste. Le legacy est
 * `student/activity/todo/TodoModal.js` (2 196 l.), une même modale pour l'élève, le tuteur en
 * séance et le parent : ici on ne dessine que le chemin PARENT. Ce qui tombe, et pourquoi :
 *  - « Afficher à l'élève », « Générer… », « Soumettre », « Valider », le partage d'écran et les
 *    outils d'annotation sont tous gardés par `hasRole(user, "tutor")` — faux en mode parent, où
 *    le jeton est celui de l'enfant ;
 *  - l'analyse structurée de la copie est gardée par `isTutorViewer` ;
 *  - « Modifier le titre » est explicitement masqué en mode parent (`TodoModal.js:1170`).
 * Reste : fermer, **Redonner** une activité terminée, **Archiver** (avec sa confirmation), et
 * **Restaurer** depuis les archives. Deux actions au plus, jamais trois.
 *
 * Contrôlé : l'activité, la vue courante, les actions offertes, l'action en cours et l'erreur
 * arrivent en props ; tout repart en callbacks, y compris les confirmations. Chaque état s'ouvre
 * seul dans une story.
 *
 * La fiche du tuteur (`<TutorActivityDetailsModal>`) la compose, sans la recopier, par des fentes
 * absentes par défaut : son menu (`headerRight`), ce qui suit l'en-tête de l'activité
 * (`headerAccessory`), son propre pied (`footer`), ce qui suit le rendu (`workFooter`), la vue du
 * quiz (`quiz`, le mode `quiz`) et ses propres surfaces de décision (`hidden`).
 */
import React, { useContext } from "react"
import { Dimensions, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { DimensionsContext } from "../../../common/utils/mobileUtils"

import {
    type UIActivityDetails,
    type UIActivityDetailsAction,
    type UIActivityDetailsDialog,
    type UIActivityDisplayMode,
} from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { HorizontalList } from "../../atoms/HorizontalList"
import { Banner } from "../../atoms/Banner"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { Modal } from "../../molecules/Modal"
import { ActivityDetailsHeader } from "../../organisms/ActivityDetailsHeader"
import { ActivityStatement } from "../../organisms/ActivityStatement"
import { ActivityWork } from "../../organisms/ActivityWork"
import { ActivityDetailsSkeleton } from "./ActivityDetailsSkeleton"

/** Le nom de chaque vue. « Rendu » prend « + correction » quand il y en a une — libellés du legacy. */
const MODE_LABELS: Record<UIActivityDisplayMode, string> = {
    material: "Énoncé",
    work: "Rendu",
    split: "Énoncé + Rendu",
    quiz: "Quizz",
}

/** Le mot de chaque geste, et son glyphe. Un seul endroit, comme `LiveSessionMenu`. */
const ACTION_LABELS: Record<UIActivityDetailsAction, { label: string; icon: "redo" | "archive" | "undo-alt" }> = {
    redo: { label: "Redonner", icon: "redo" },
    archive: { label: "Archiver", icon: "archive" },
    restore: { label: "Restaurer", icon: "undo-alt" },
}

/**
 * Les mots des trois surfaces de décision, au parent. Le legacy tutoie l'élève et vouvoie le
 * parent par une table jumelle (`TodoModal.js`, `TEXTS.parent`) : ce sont ces phrases-là.
 */
const DIALOGS: Record<
    UIActivityDetailsDialog,
    // `notice` ANNONCE (un seul bouton, comme le `DialogM` legacy sans `buttonText2`) ;
    // `confirm` DEMANDE, et garde sa sortie.
    { kind: "confirm" | "notice"; title: string; message: string; confirmLabel: string }
> = {
    archive: {
        kind: "confirm",
        title: "Voulez-vous archiver cette activité ?",
        message: "Elle pourra être restaurée depuis les activités archivées.",
        confirmLabel: "Archiver",
    },
    deleteDenied: {
        kind: "notice",
        title: "Non autorisé",
        message: "L'activité a été créée par le tuteur, elle ne peut pas être supprimée.",
        confirmLabel: "OK",
    },
    updateDenied: {
        kind: "notice",
        title: "Non autorisé",
        message: "L'activité a été créée par le tuteur, elle ne peut pas être modifiée.",
        confirmLabel: "OK",
    },
}

export type ActivityDetailsModalProps = {
    visible: boolean
    onClose: () => void
    /** L'activité. `null` → la fiche ne rend rien : c'est l'écran qui décide quand elle est prête. */
    activity: UIActivityDetails | null
    /** L'activité se lit : la fiche, ouverte dès le geste, en montre la forme (`<ActivityDetailsSkeleton>`). */
    loading?: boolean

    /** Les vues offertes, dans l'ordre. Une seule → pas de sélecteur. */
    modes?: UIActivityDisplayMode[]
    mode?: UIActivityDisplayMode
    onModeChange?: (mode: UIActivityDisplayMode) => void

    /** Les gestes offerts. Deux au plus : l'étape pose une question, elle n'ouvre pas un menu. */
    actions?: UIActivityDetailsAction[]
    onAction?: (action: UIActivityDetailsAction) => void
    /** Le geste en cours — c'est son bouton qui tourne. */
    submitting?: UIActivityDetailsAction | null

    /** La surface de décision ouverte. `null` → aucune. */
    dialog?: UIActivityDetailsDialog | null
    onConfirmDialog?: () => void
    onCancelDialog?: () => void

    /** Un échec à montrer dans la fiche — le legacy le renvoie hors modale. */
    error?: string | null
    title?: string

    // ── Les fentes du tuteur (`<TutorActivityDetailsModal>`) — absentes : la fiche du parent ──
    /** À droite de l'en-tête de la feuille — le menu du tuteur. */
    headerRight?: React.ReactNode
    /** Sous l'en-tête de l'activité — « Quiz d'abord · Retirer », l'élève qui la regarde. */
    headerAccessory?: React.ReactNode
    /** Le pied de la feuille, à la place de celui que `actions` construit. */
    footer?: React.ReactNode
    /** Sous le rendu — l'avis du tuteur sur la correction de Sam. */
    workFooter?: React.ReactNode
    /** La vue « Quizz » (mode `quiz`). */
    quiz?: React.ReactNode
    /** La feuille s'efface sans se démonter, sous une surface de décision de l'appelant. */
    hidden?: boolean
    testID?: string
}

export function ActivityDetailsModal(props: ActivityDetailsModalProps) {
    const {
        visible,
        onClose,
        activity,
        loading = false,
        modes: offeredModes = ["material"],
        onModeChange,
        actions = [],
        onAction,
        submitting = null,
        dialog = null,
        onConfirmDialog,
        onCancelDialog,
        error = null,
        title = "Activité",
        headerRight,
        headerAccessory,
        footer: footerSlot,
        workFooter,
        quiz,
        hidden = false,
        testID,
    } = props
    // Sans vue « Quizz » fournie, sa puce ouvrirait un corps vide : elle n'est pas offerte.
    const modes = quiz == null ? offeredModes.filter((m) => m !== "quiz") : offeredModes
    const mode = props.mode ?? modes[0] ?? "material"

    const split = mode === "split"
    const showsStatement = mode === "material" || split
    const showsWork = mode === "work" || split
    const showsQuiz = mode === "quiz" && quiz != null
    // Grand écran, lu comme `<Modal>` le lit (carte centrée au-delà de 800 px) : la fiche ne défile
    // plus d'un bloc. L'en-tête reste, chaque colonne défile seule, la copie à pastilles remplit la
    // sienne. Sur un téléphone, la fiche défile d'un bloc, comme avant.
    const dimensions = useContext(DimensionsContext) ?? Dimensions.get("window")
    const roomy = dimensions.width > 800
    const interactiveCopy = activity?.work?.copyPages != null && activity.work.correctionPoints != null
    // La copie à pastilles a besoin de place à côté de son point : l'énoncé lui cède une part.
    const statementWeight = split && interactiveCopy ? 2 : 1
    const workWeight = split && interactiveCopy ? 3 : 1

    const modeLabel = (m: UIActivityDisplayMode) =>
        m === "work" && activity?.work?.hasCorrection === true ? "Rendu + correction" : MODE_LABELS[m]
    const modeChips = modes.map((m) => (
        <ChoiceChip key={m} label={modeLabel(m)} selected={m === mode} onPress={() => onModeChange?.(m)} />
    ))
    const errorBanner = error != null && error !== "" && (
        <Banner color="danger" tinted icon="exclamation-circle" message={error} />
    )

    const footer =
        footerSlot !== undefined ? (
            footerSlot
        ) : actions.length > 0 ? (
            <Row gap="2" justify="center">
                {actions.map((action, index) => (
                    <Button
                        key={action}
                        label={ACTION_LABELS[action].label}
                        iconName={ACTION_LABELS[action].icon}
                        variant={index === 0 ? "solid" : "outline"}
                        color={action === "archive" ? "danger" : "primary"}
                        loading={submitting === action}
                        disabled={submitting != null && submitting !== action}
                        onPress={() => onAction?.(action)}
                    />
                ))}
            </Row>
        ) : undefined

    const openDialog = dialog != null ? DIALOGS[dialog] : null

    return (
        <>
            {/* Une surface de décision efface la fiche, comme dans le legacy : `onDeletePress`
                ferme la modale (`handleShowModal()`) AVANT d'ouvrir le dialogue. `hidden` garde
                la fiche montée, donc son état, et règle au passage l'ordre des portails — deux
                voiles empilés se peignent dans l'ordre de montage, pas dans celui du code. */}
            <Modal
                visible={visible}
                onClose={onClose}
                // En grand écran, le titre de l'activité prend l'en-tête : « Activité » n'apprenait rien.
                title={roomy && activity != null ? activity.title : title}
                scrollable={!roomy}
                fullScreenOnSmall
                dismissable
                hidden={dialog != null || hidden}
                // Large dès que l'écran double est proposé : changer d'onglet ne fait pas sauter la carte.
                wide={modes.includes("split")}
                footer={footer}
                headerRight={headerRight}
                testID={testID}
            >
                {activity == null && loading && (
                    <DelayedSkeleton accessibilityLabel="Chargement de l'activité">
                        <ActivityDetailsSkeleton />
                    </DelayedSkeleton>
                )}
                {activity != null && roomy && (
                    <View style={styles.roomy}>
                        <Row gap="3" justify="between" style={styles.roomyHead}>
                            <View style={styles.shrink}>
                                <ActivityDetailsHeader activity={activity} showTitle={false} />
                                {headerAccessory}
                            </View>
                            {modes.length > 1 && <Row gap="2">{modeChips}</Row>}
                        </Row>
                        {errorBanner}
                        {showsQuiz && (
                            <ScrollView style={styles.column(1)} showsVerticalScrollIndicator={false}>
                                {quiz}
                            </ScrollView>
                        )}
                        {/* « Énoncé + Rendu » : côte à côte, comme la TodoModal à partir de 800 px. */}
                        {!showsQuiz && (
                            <View style={styles.columns}>
                                {showsStatement && (
                                    <ScrollView
                                        style={styles.column(statementWeight)}
                                        showsVerticalScrollIndicator={false}
                                    >
                                        <ActivityStatement statement={activity.statement} />
                                    </ScrollView>
                                )}
                                {showsWork &&
                                    activity.work != null &&
                                    (interactiveCopy ? (
                                        <View style={styles.column(workWeight)}>
                                            <ActivityWork work={activity.work} fill />
                                        </View>
                                    ) : (
                                        <ScrollView
                                            style={styles.column(workWeight)}
                                            showsVerticalScrollIndicator={false}
                                        >
                                            <ActivityWork work={activity.work} />
                                        </ScrollView>
                                    ))}
                            </View>
                        )}
                        {showsWork && !showsQuiz && workFooter}
                    </View>
                )}
                {activity != null && !roomy && (
                    <Stack gap="5">
                        <ActivityDetailsHeader activity={activity} />
                        {headerAccessory}
                        {errorBanner}
                        {modes.length > 1 && <HorizontalList gap="2">{modeChips}</HorizontalList>}
                        {showsQuiz && quiz}
                        {showsStatement && <ActivityStatement statement={activity.statement} />}
                        {showsWork && activity.work != null && <ActivityWork work={activity.work} />}
                        {showsWork && workFooter}
                    </Stack>
                )}
            </Modal>

            {openDialog != null && (
                <ConfirmDialog
                    visible
                    title={openDialog.title}
                    message={openDialog.message}
                    confirmLabel={openDialog.confirmLabel}
                    cancelLabel="Annuler"
                    hideCancel={openDialog.kind === "notice"}
                    danger={openDialog.kind === "confirm"}
                    onConfirm={() => (openDialog.kind === "notice" ? onCancelDialog?.() : onConfirmDialog?.())}
                    onCancel={() => onCancelDialog?.()}
                />
            )}
        </>
    )
}

const styles = StyleSheet.create((theme) => ({
    roomy: {
        flex: 1,
        minHeight: 0,
        gap: theme.spacing["4"],
    },
    roomyHead: {
        flexWrap: "wrap" as const,
    },
    shrink: {
        flexShrink: 1,
    },
    columns: {
        flex: 1,
        minHeight: 0,
        flexDirection: "row" as const,
        gap: theme.spacing["6"],
    },
    column: (weight: number) => ({
        flex: weight,
        minWidth: 0,
    }),
}))
